import React from "react";

import {
  TRACKING_PIXEL_TYPES,
  TrackingPixel,
  TrackingPixelType,
} from "src/types/experiences/storyboardSettings/tracking-pixel";

import Card from "../card";
import { useDraft } from "../useDraft";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import { DropDown } from "../dropdown";
import styles from "./trackingPixelsCard.module.scss";

const PIXEL_LABELS: Record<TrackingPixelType, string> = {
  GOOGLE_ANALYTICS: "Google Analytics",
  GOOGLE_TAG_MANAGER: "Google Tag Manager",
  FACEBOOK: "Facebook",
  LINKEDIN: "LinkedIn",
  MUNCHKIN: "Munchkin (Marketo)",
  SIXSENSE: "6sense",
};

const newUid = () =>
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const TrackingPixelsCard: React.FC = () => {
  const { draft, setField } = useDraft();
  const pixels: TrackingPixel[] = draft?.trackingPixels ?? [];
  const editable = canEdit(draft?.role);

  const updatePixels = (next: TrackingPixel[]) =>
    setField("trackingPixels", next);

  const addPixel = (type: TrackingPixelType) => {
    updatePixels([...pixels, { uid: newUid(), type, value: "" }]);
  };

  const updateValue = (uid: string, value: string) => {
    updatePixels(pixels.map((p) => (p.uid === uid ? { ...p, value } : p)));
  };

  const removePixel = (uid: string) => {
    updatePixels(pixels.filter((p) => p.uid !== uid));
  };

  const headerRight = (
    <DropDown
      selected={{ key: "", label: "Add Pixel" }}
      placeholder="Add Pixel"
      disabled={!editable}
      options={Object.keys(TRACKING_PIXEL_TYPES).map((type) => ({
        key: type,
        label: PIXEL_LABELS[type as TrackingPixelType],
      }))}
      onChange={(key) => addPixel(key as TrackingPixelType)}
      className={styles.dropdown}
      align="end"
    />
  );

  return (
    <Card
      title="Tracking Pixels"
      description="Pixels help track page traffic, interactions, conversions and more"
      headerRight={headerRight}
    >
      {!!pixels?.length && (
        <div className={styles.pixelList}>
          {pixels.map((pixel, index) => (
            <div key={pixel.uid} className={styles.pixelRow}>
              {pixel.type !== pixels[index - 1]?.type && (
                <div className={styles.typeLabel}>
                  {PIXEL_LABELS[pixel.type]}
                </div>
              )}
              <div className={styles.verificationItem}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Enter tracking pixel code"
                  value={pixel.value}
                  disabled={!editable}
                  onChange={(e) => updateValue(pixel.uid, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removePixel(pixel.uid)}
                  disabled={!editable}
                  aria-label="Remove pixel"
                  className={styles.removeBtn}
                >
                  <i className="paperflite-close" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default TrackingPixelsCard;
